import { PaginationParams } from '@/lib/types';
import { api } from './api';
import apiClient from './api';

const getProducts = async (clinicId: string, pagination: PaginationParams) => {
  const queryParams = new URLSearchParams();
  if (pagination.page) queryParams.append('page', pagination.page.toString());
  if (pagination.limit) queryParams.append('limit', pagination.limit.toString());
  if (pagination.sortBy) queryParams.append('sortBy', pagination.sortBy);
  if (pagination.sortOrder) queryParams.append('sortOrder', pagination.sortOrder);
  if (pagination.search) queryParams.append('search', pagination.search);

  const response = await api.get(`products/clinic/${clinicId}?${queryParams.toString()}`);
  return response.data;
};

const setClinicPrice = async (productId: string, clinicId: string, price: number) => {
  const response = await api.post(`products/${productId}/set-clinic-price`, { 
    price, 
    clinicId 
  });
  return response.data;
};

const updateClinicCompoundingReason = async (
  productId: string,
  clinicId: string,
  reasonForCompoundingContext: string | null
) => {
  const response = await api.put(
    `products/${productId}/clinic/${clinicId}/compounding-reason`,
    {
      reason_for_compounding_context: reasonForCompoundingContext,
    }
  );
  return response.data;
};

const exportClinicProducts = async (clinicId: string, search?: string): Promise<Blob> => {
  const queryParams = new URLSearchParams();
  if (search) queryParams.append('search', search);

  // Use apiClient directly for blob responses to avoid the wrapper extracting data incorrectly
  const response = await apiClient.get(`products/clinic/${clinicId}/export?${queryParams.toString()}`, {
    responseType: 'blob',
  });
  return response.data;
};

const previewBulkUpdateFromExcel = async (clinicId: string, file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(`products/clinic/${clinicId}/bulk-update-preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data?.data ?? response.data;
};

export interface BulkPriceUpdate {
  id: string;
  selling_price_dollars: number;
}

export interface BulkUpdateResult {
  updated: number;
  skipped: number;
  errors: Array<{
    id: string;
    description?: string;
    error: string;
  }>;
  updatedProducts: Array<{
    id: string;
    description: string;
    ndc: string;
    base_price_dollars: number;
    clinic_price_dollars: number;
  }>;
}

const bulkUpdateClinicProducts = async (
  clinicId: string, 
  updates: BulkPriceUpdate[]
): Promise<{ success: boolean; message: string; data: BulkUpdateResult }> => {
  // api.post already extracts response.data, so we get the full API response object
  const response = await api.post(`products/clinic/${clinicId}/bulk-update`, { 
    updates 
  });
  return response; // Return the full response { success, message, data }
};

const bulkUpdateClinicProductsAsync = async (
  clinicId: string,
  payload: { updates: BulkPriceUpdate[]; notificationEmail: string }
): Promise<{ success: boolean; message: string; jobId: string }> => {
  const response = await api.post(`products/clinic/${clinicId}/bulk-update-async`, payload);
  return response;
};

// Fetch current prices for specific product IDs to compare against uploaded Excel
const getProductCurrentPrices = async (
  clinicId: string,
  productIds: string[]
): Promise<Map<string, number>> => {
  // Get all products with current search empty to fetch by IDs
  // We'll filter to the IDs we need
  const response = await api.get(`products/clinic/${clinicId}?limit=10000`);
  const products = response.data?.products || [];
  
  const priceMap = new Map<string, number>();
  products.forEach((product: any) => {
    if (productIds.includes(product.id)) {
      priceMap.set(product.id, product.clinic_price_dollars || 0);
    }
  });
  
  return priceMap;
};

// Price history types
export interface PriceHistoryEntry {
  id: string;
  product_id: string;
  product_description: string;
  product_ndc: string;
  clinic_id: string;
  price_type: 'base_price' | 'selling_price';
  old_value_cents: number | null;
  old_value_dollars: number | null;
  new_value_cents: number;
  new_value_dollars: number;
  change_amount_dollars: number | null;
  change_source: 'manual' | 'bulk_excel';
  changed_by_user_id: string;
  changed_by_user_name: string;
  changed_by_user_role: string;
  changed_by_user_email: string;
  changed_by_user_ip_address: string | null;
  changed_at: string;
  is_initial_setting: boolean;
  is_price_increase: boolean;
  is_price_decrease: boolean;
}

// Get product price history
const getProductPriceHistory = async (
  clinicId: string, 
  productId: string, 
  priceType?: 'base_price' | 'selling_price'
): Promise<PriceHistoryEntry[]> => {
  const queryParams = new URLSearchParams();
  if (priceType) queryParams.append('priceType', priceType);
  
  const response = await api.get(
    `products/clinic/${clinicId}/product/${productId}/price-history?${queryParams.toString()}`
  );
  return response.data;
};

const getProductsForPatient = async (
  clinicId: string,
  patientId: string,
  pagination: PaginationParams
) => {
  const queryParams = new URLSearchParams()
  if (pagination.page) queryParams.append('page', pagination.page.toString())
  if (pagination.limit) queryParams.append('limit', pagination.limit.toString())
  if (pagination.sortBy) queryParams.append('sortBy', pagination.sortBy)
  if (pagination.sortOrder) queryParams.append('sortOrder', pagination.sortOrder)
  if (pagination.search) queryParams.append('search', pagination.search)

  const response = await api.get(
    `products/clinic/${clinicId}/for-patient/${patientId}?${queryParams.toString()}`
  )
  return response.data
}

const updateProductPotentialAllergies = async (
  productId: string,
  potentialAllergies: string[]
): Promise<any> => {
  const response = await api.put(`products/${productId}/potential-allergies`, {
    potential_allergies: potentialAllergies,
  });
  return response.data;
};

const checkAllergyInteractions = async (
  patientId: string,
  productIds: string[],
  medicationNames: string[]
): Promise<{ hasWarnings: boolean; warnings: Array<{ productId: string | null; productDescription: string; matchedAllergy: string; productAllergyTag: string }> }> => {
  const response = await api.post('clinic/patients/check-allergy-interactions', {
    patientId,
    productIds,
    medicationNames,
  });
  return response.data;
};

export {
  getProducts,
  getProductsForPatient,
  setClinicPrice,
  updateClinicCompoundingReason,
  exportClinicProducts,
  previewBulkUpdateFromExcel,
  bulkUpdateClinicProducts,
  bulkUpdateClinicProductsAsync,
  getProductCurrentPrices,
  getProductPriceHistory,
  updateProductPotentialAllergies,
  checkAllergyInteractions,
};
